"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const types_1 = require("../config/types");
// Map of OUR chains to the chain names used by llama in their `poolsOld`
const LlamaChains = {
    [types_1.Networks.Ropsten]: "ropsten",
    [types_1.Networks.Mainnet]: "ethereum",
    [types_1.Networks.Polygon]: "polygon",
    [types_1.Networks.Arbitrum]: "arbitrum",
    [types_1.Networks.Avalanche]: "avalanche",
    [types_1.Networks.Moonriver]: "moonriver",
    [types_1.Networks.Aurora]: "aurora",
    [types_1.Networks.Cronos]: "cronos",
    [types_1.Networks.Fantom]: "fantom",
    [types_1.Networks.Harmony]: "harmony",
    [types_1.Networks.Moonbeam]: "moonbeam",
    [types_1.Networks.Sxnetwork]: "sxnetwork",
    [types_1.Networks.Canto]: "canto",
    [types_1.Networks.ZksyncEra]: "zksync era",
    [types_1.Networks.Base]: "base",
    [types_1.Networks.Mantle]: "mantle",
    [types_1.Networks.Scroll]: "scroll",
    [types_1.Networks.Optimism]: "optimism",
    [types_1.Networks.Real]: "re.al",
    [types_1.Networks.Blast]: "blast",
    [types_1.Networks.Sonic]: "sonic",
    [types_1.Networks.Linea]: "linea",
};
// Class to get the APR from DefiLlama directly, only makes 1 call to api and caches all pools
class OffchainAPRHelper {
    constructor(offchain) {
        this.llamaAllPools = {};
        this.initialized = null;
        this.chainTvls = {};
        this.tvlInitialized = null;
        this.chainsTotalSupplied = {};
        this.totalSuppliedInitialized = null;
        // Charts
        this.chainTvlCharts = {};
        this.protocolTvlChart = [];
        this.shadowAllPools = {};
        this.shadowInitialized = null;
        this.nileAllPools = {};
        this.nileInitialized = null;
        this.blacklistedProjects = new Set([
            "extra-finance",
            "beefy",
            "impermax-finance",
        ]);
        this.offchain = offchain;
    }
    cleanCache() {
        this.llamaAllPools = {};
        this.initialized = null;
        this.chainTvls = {};
        this.tvlInitialized = null;
        this.chainsTotalSupplied = {};
        this.totalSuppliedInitialized = null;
    }
    // Blacklist certain projects which might report same pool as dex (aggregators, etc.)
    // Reason we need this is that we want the DEX APR and not the APR of other projects
    // using that same LP on their strategies, and sometimes pools_old from defillama
    // shares lp address across different projects.
    isBlacklisted(project) {
        return this.blacklistedProjects.has(project);
    }
    // Initializes all llama pools
    initializeYields() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield fetch("https://yields.llama.fi/poolsOld").then((i) => i.json());
                const pools = response.data;
                const filteredPools = pools.filter((i) => !this.isBlacklisted(i.project));
                // i.pool_old is the pool address with sometimes -{chain} separator if
                // contracts exists on diff chains
                this.llamaAllPools = filteredPools.reduce((acc, i) => {
                    const pool = i.pool_old.split("-")[0];
                    const llamaChain = i.chain;
                    const llamaId = pool.concat("-", llamaChain).toLowerCase();
                    acc[llamaId] = i;
                    return acc;
                }, {});
            }
            catch (err) {
                console.warn("The llamas yields down?");
                this.llamaAllPools = {};
            }
        });
    }
    // Initializes all our chain tvl and borrows from llama
    initializeTvls() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield fetch("https://api.llama.fi/protocol/impermax-finance").then((i) => i.json());
                // Simple TVL of all chains
                const currentChainTvls = response.currentChainTvls;
                // For charts of our tvl and tvl by chains
                const chainTvlCharts = response.chainTvls;
                const protocolTvlChart = response.tvl;
                // Get the chain tvls
                this.chainTvls = Object.entries(currentChainTvls).reduce((acc, [chain, tvl]) => {
                    acc[chain.toLowerCase()] = tvl;
                    return acc;
                }, {});
                // Get the chain tvls day data to build charts
                this.chainTvlCharts = Object.entries(chainTvlCharts).reduce((acc, [chain, dayData]) => {
                    acc[chain.toLowerCase()] = dayData.tvl;
                    return acc;
                }, {});
                this.protocolTvlChart = protocolTvlChart;
            }
            catch (err) {
                console.warn("The llamas TVL down?");
                this.chainTvls = {};
                this.protocolTvlChart = [];
                this.chainTvlCharts = {};
            }
        });
    }
    // Initializes the `totalSupplyUsd` from our yields
    initializePoolsBorrow() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield fetch("https://yields.llama.fi/poolsBorrow").then((i) => i.json());
                const impermaxPools = response.data.filter((i) => i.project === "impermax-finance");
                this.chainsTotalSupplied = impermaxPools.reduce((acc, pool) => {
                    acc[pool.chain.toLowerCase()] =
                        (acc[pool.chain.toLowerCase()] || 0) + pool.totalSupplyUsd;
                    return acc;
                }, {});
            }
            catch (err) {
                console.warn("The llamas poolsBorrow down?");
                this.chainsTotalSupplied = {};
            }
        });
    }
    initializeShadowPools() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield fetch("https://api.shadow.so/mixed-pairs").then((i) => i.json());
                this.shadowAllPools = response.pairs.reduce((acc, pool) => {
                    acc[pool.id.toLowerCase()] = {
                        lpApr: pool.lpApr
                    };
                    return acc;
                }, {});
            }
            catch (err) {
                console.warn("Shadow API down?");
                this.shadowAllPools = {};
            }
        });
    }
    initializeNilePools() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield fetch("https://nile-api-production.up.railway.app/mixed-pairs").then((i) => i.json());
                this.nileAllPools = response.pairs
                    .filter((pair) => pair.symbol.includes("vAMM-") || pair.symbol.includes("cAMM-"))
                    .reduce((acc, pool) => {
                    acc[pool.id] = {
                        lpApr: pool.lpApr / 1e2
                    };
                    return acc;
                }, {});
            }
            catch (err) {
                console.warn("Shadow API down?");
                this.shadowAllPools = {};
            }
        });
    }
    getLlamaYields() {
        return __awaiter(this, void 0, void 0, function* () {
            // First call initializes
            if (!this.initialized)
                this.initialized = this.initializeYields();
            // async mutex to only call llama api once
            yield this.initialized;
            return this.llamaAllPools;
        });
    }
    getLlamaTvls() {
        return __awaiter(this, void 0, void 0, function* () {
            // First call initializes
            if (!this.tvlInitialized)
                this.tvlInitialized = this.initializeTvls();
            // async mutex to only call llama api once
            yield this.tvlInitialized;
            return this.chainTvls;
        });
    }
    getChainsTotalSupplied() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.totalSuppliedInitialized)
                this.totalSuppliedInitialized = this.initializePoolsBorrow();
            yield this.totalSuppliedInitialized;
            return this.chainsTotalSupplied;
        });
    }
    getShadowPools() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.shadowInitialized)
                this.shadowInitialized = this.initializeShadowPools();
            yield this.shadowInitialized;
            return this.shadowAllPools;
        });
    }
    getNilePools() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.nileInitialized)
                this.nileInitialized = this.initializeNilePools();
            yield this.nileInitialized;
            return this.nileAllPools;
        });
    }
    // ---------------- Getters ---------------- //
    getLlamaAPR(pool, chain) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // Get or initialize the pools
            const llamaAllPools = yield this.getLlamaYields();
            // Map to llama chain names, if not supported, then fetch native way
            const llamaChain = LlamaChains[chain];
            if (!llamaChain)
                return 0;
            // Make pool unique ({poolAddress}-{chain})
            const llamaId = pool.concat("-", llamaChain).toLowerCase();
            // Convert to decimals to match `getUniswapAPR` and `getStakingAPR`
            const apy = ((_a = llamaAllPools[llamaId]) === null || _a === void 0 ? void 0 : _a.apy) / 100;
            // If 0 then should fetch native way to make sure
            return apy || 0;
        });
    }
    // Shadow APRs for Shadow on Sonic chain only
    getShadowAPR(pool, chain) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // Guard for Sonic network only (for now..)
            if (chain !== types_1.Networks.Sonic)
                return 0;
            // Get or initialize the Shadow pools
            const shadowPools = yield this.getShadowPools();
            // `initializeShadowPools` keys by pool address
            // Convert to decimals to match `getUniswapAPR` and `getStakingAPR`
            const apy = ((_a = shadowPools[pool.toLowerCase()]) === null || _a === void 0 ? void 0 : _a.lpApr) / 100;
            return apy || 0;
        });
    }
    // Nile APRs for Nile on Linea chain only
    getNileAPR(pool, chain) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // Guard for Sonic network only (for now..)
            if (chain !== types_1.Networks.Linea)
                return 0;
            // Get or initialize the Nile pools
            const nilePools = yield this.getNilePools();
            // `initializeNilePools` keys by pool address
            // Convert to decimals to match `getUniswapAPR` and `getStakingAPR`
            const apy = (_a = nilePools[pool.toLowerCase()]) === null || _a === void 0 ? void 0 : _a.lpApr;
            return apy || 0;
        });
    }
    getLlamaTvlAndBorrows(chain) {
        return __awaiter(this, void 0, void 0, function* () {
            // Get or initialize the tvl for all chains
            const [llamaTvls, totalSuppliedYields] = yield Promise.all([
                this.getLlamaTvls(),
                this.getChainsTotalSupplied(),
            ]);
            const llamaChain = LlamaChains[chain];
            if (!llamaChain)
                return { tvl: 0, borrowed: 0, totalSupplied: 0 };
            const tvl = llamaTvls[llamaChain];
            const borrowed = llamaTvls[llamaChain.concat("-borrowed")];
            const totalSupplied = totalSuppliedYields[chain];
            return { tvl, borrowed, totalSupplied };
        });
    }
    // Overall Cross-chain stats
    getImpermaxBorrows() {
        return __awaiter(this, void 0, void 0, function* () {
            const llamaTvls = yield this.getLlamaTvls();
            return llamaTvls.borrowed;
        });
    }
    getImpermaxTvl() {
        return __awaiter(this, void 0, void 0, function* () {
            const llamaTvls = yield this.getLlamaTvls();
            let crossChainTvl = 0;
            // LLama key is `{chain}` or `{chain}-borrowed`, need to manually add the tvl
            for (const [chain, tvl] of Object.entries(llamaTvls)) {
                if (!chain.includes("borrowed"))
                    crossChainTvl += tvl;
            }
            return crossChainTvl;
        });
    }
    // Charts
    // Impermax historic TVL chart
    getProtocolTvlChart() {
        return __awaiter(this, void 0, void 0, function* () {
            // Initialize tvls
            yield this.getLlamaTvls();
            return this.protocolTvlChart;
        });
    }
    // Impermax historic TVL chart by chain
    getChainTvlChart(chain) {
        return __awaiter(this, void 0, void 0, function* () {
            // Initialize tvls
            yield this.getLlamaTvls();
            const llamaChain = LlamaChains[chain];
            return this.chainTvlCharts[llamaChain];
        });
    }
}
exports.default = OffchainAPRHelper;
